import styles from "./polygon-on-map-modal.module.css";
import {
  useEffect,
  useMemo,
  createContext,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Input,
  Form,
  Modal,
  Flex,
  Button,
  Typography,
  Checkbox,
  Tooltip,
  Upload,
  message,
} from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  verticalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import TrashIcon from "@/icons/TrashIcon";
import PlusIcon from "@/icons/PlusIcon";
import CheckIcon from "@/icons/CheckIcon";
import DuplicateIcon from "@/icons/DuplicateIcon";
import ReplyIcon from "@/icons/ReplyIcon";
import DocumentAddIcon from "@/icons/DocumentAddIcon";

import InputPolygonOnMap from "@/components/inputs/InputPolygonOnMap";
import DragHandle from "@/components/ui/DragHandle";

import {
  COORDINATE_RULE,
  LATITUDE_RULE,
  LONGITUDE_RULE,
  isValidCoordinate,
} from "@/constants/map";

import { useCopySelectedPoints } from "@/hooks/useCopySelectedPoints";
import { useUndoPoints } from "@/hooks/useUndoPoints";

const MODAL_WIDTH = 1300;
const FORM_WIDTH = 500;
const LIST_MAX_HEIGHT = 600;

const { Text } = Typography;

const SortableListItemContext = createContext({});

function parseClipboardPoints(text) {
  const cleaned = text.trim();

  if (!cleaned) return [];

  const hasTabs = cleaned.includes("\t");
  const hasNewLines = cleaned.includes("\n");

  // TAB / ENTER
  if (hasTabs || hasNewLines) {
    return cleaned
      .split(/\n+/)
      .map((line) =>
        line
          .split(/\t|[,; ]+/)
          .map((v) => parseFloat(v.replace(",", ".")))
          .filter((v) => !Number.isNaN(v)),
      )
      .filter((pair) => pair.length >= 2)
      .map(([lat, lon]) => ({ lat, lon }));
  }

  // Запятые, точки с запятыми и пробелы
  const numbers = cleaned
    .split(/[,; ]+/)
    .map((v) => parseFloat(v.replace(",", ".")))
    .filter((v) => !Number.isNaN(v));
  const points = [];

  for (let i = 0; i < numbers.length; i += 2) {
    points.push({
      lat: numbers[i],
      lon: numbers[i + 1] ?? null, // не заполняется, если нечетное количество
    });
  }

  return points;
}

function parseCSVPoints(text) {
  if (!text) return [];

  const rows = text
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  return rows
    .map((line) => {
      const parts = line.split(/[,;]+/).map((v) => v.trim());

      const lat = parseFloat(parts[0]?.replace(",", "."));
      const lon = parseFloat(parts[1]?.replace(",", "."));

      return { lat, lon };
    })
    .filter(
      ({ lat, lon }) =>
        isValidCoordinate(lat, -90, 90) && isValidCoordinate(lon, -180, 180),
    );
}

// Окно для установки полигона на карте, содержит интерактивную карту и форму с координатами
const PolygonOnMapModal = ({
  field,
  data,
  visible,
  onSave,
  onCancel,
  color,
  objectUuid,
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      form.setFieldsValue({
        points: [],
      });
      return;
    }

    // Преобразуем массив точек в формат формы
    const points = data.map(({ lat, lon }) => ({
      lat: lat !== null && lat !== undefined ? lat : null,
      lon: lon !== null && lon !== undefined ? lon : null,
    }));

    form.setFieldsValue({ points });
  }, [data, form]);

  // Убираем таб отовсюду, кроме инпутов
  useEffect(() => {
    if (!visible) return;

    const modal = document.querySelector(".ant-modal");
    if (!modal) return;

    const focusables = modal.querySelectorAll(
      "button, a, [tabindex], input, select, textarea, span",
    );

    const originalTabIndexes = new Map();

    focusables.forEach((el) => {
      originalTabIndexes.set(el, el.getAttribute("tabindex"));

      if (el.tagName === "INPUT" && el.type !== "checkbox") {
        el.removeAttribute("tabindex");
      } else {
        el.setAttribute("tabindex", "-1");
      }
    });

    return () => {
      focusables.forEach((el) => {
        const original = originalTabIndexes.get(el);

        if (original === null) {
          el.removeAttribute("tabindex");
        } else {
          el.setAttribute("tabindex", original);
        }
      });
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    const modal = document.querySelector(".ant-modal");
    if (!modal) return;

    const handler = (e) => {
      if (e.key !== "Tab") return;

      const inputs = modal.querySelectorAll("input:not([disabled])");
      if (!inputs.length) return;

      const first = inputs[0];
      const last = inputs[inputs.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal.addEventListener("keydown", handler);
    return () => modal.removeEventListener("keydown", handler);
  }, [visible]);

  // Автофокус на первый инпут
  useEffect(() => {
    if (!visible) return;

    const modal = document.querySelector(".ant-modal");
    const firstInput = modal?.querySelector("input");

    firstInput?.focus();
  }, [visible]);

  const [highlighted, setHighlighted] = useState(new Set());
  const [hoverSource, setHoverSource] = useState(null);

  const listRef = useRef(null);
  const itemRefs = useRef({});

  // Очистка подсветки
  useEffect(() => {
    const clear = () => setHighlighted(new Set());
    document.addEventListener("click", clear);
    return () => document.removeEventListener("click", clear);
  }, []);

  // Подкрутка к подсветке
  useEffect(() => {
    if (hoveredIndex == null) return;
    if (hoverSource !== "map") return; // Чтобы не мешало скроллу списка

    const el = itemRefs.current[hoveredIndex];
    const container = listRef.current;

    if (!el || !container) return;

    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const offsetTop = elRect.top - containerRect.top;
    const offsetBottom = elRect.bottom - containerRect.bottom;

    if (offsetTop < 0) {
      container.scrollBy({
        top: offsetTop - 8,
        behavior: "smooth",
      });
    } else if (offsetBottom > 0) {
      container.scrollBy({
        top: offsetBottom + 8,
        behavior: "smooth",
      });
    }
  }, [hoveredIndex, hoverSource]);

  // Получаем текущие значения формы
  const formValues = Form.useWatch([], form);

  // Мемоизируем точки для передачи в InputPolygonOnMap
  const currentPoints = useMemo(() => {
    if (formValues?.points && Array.isArray(formValues.points)) {
      // Фильтруем только валидные точки с обеими координатами
      const validPoints = formValues.points
        .filter((point) => {
          if (!point) return false;
          return (
            isValidCoordinate(point.lat, -90, 90) &&
            isValidCoordinate(point.lon, -180, 180)
          );
        })
        .map((point, index) => ({
          lat: parseFloat(point.lat),
          lon: parseFloat(point.lon),
          // Добавляем индекс для создания стабильного ключа
          _index: index,
        }));

      return validPoints.length > 0 ? validPoints : null;
    }
    return data && data.length > 0 ? data : null;
  }, [formValues, data]);

  // Копирование координат
  const [selectedPoints, setSelectedPoints] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const { copied, copy } = useCopySelectedPoints({
    selectedPoints,
    points: formValues?.points,
  });

  // Удаление выделенных точек
  const handleRemoveSelected = () => {
    if (!selectedPoints.size) return;

    const current = [...(form.getFieldValue("points") || [])];

    const sortedIndexes = Array.from(selectedPoints).sort((a, b) => b - a);

    sortedIndexes.forEach((index) => {
      current.splice(index, 1);
    });

    form.setFieldsValue({ points: current });
    commit(current);

    setSelectedPoints(new Set());
    setSelectAll(false);
    setHighlighted(new Set());
  };

  // Отмена (undo)
  const blurTimeout = useRef(null);
  const setPoints = (points) => {
    form.setFieldsValue({ points });
  };
  const { undo, canUndo, init, commit } = useUndoPoints({
    setPoints,
  });

  useEffect(() => {
    if (!visible) return;

    const initialPoints = (data || []).map(({ lat, lon }) => ({
      lat: lat ?? null,
      lon: lon ?? null,
    }));

    form.setFieldsValue({ points: initialPoints });
    init(initialPoints);
  }, [visible, data, form, init]);

  // Обработчик изменения точек на карте
  const handleChangePoints = (points) => {
    if (points && Array.isArray(points)) {
      form.setFieldsValue({
        points: points.map(({ lat, lon }) => ({ lat, lon })),
      });
      commit(points.map(({ lat, lon }) => ({ lat, lon })));

      if (error) {
        setError(null);
      }
    }
  };

  // Обработчик вставки значений
  const handlePastePoints = (e, index) => {
    const clipboardText = e.clipboardData?.getData("text");
    if (!clipboardText) return;

    const pointsFromClipboard = parseClipboardPoints(clipboardText);

    // если точка одна - это не paste
    if (
      pointsFromClipboard.length === 1 &&
      pointsFromClipboard[0].lon === null
    ) {
      return;
    }

    if (!pointsFromClipboard.length) return;

    e.preventDefault();

    const current = [...(form.getFieldValue("points") || [])];

    const currentPoint = current[index];

    const isEmptyCurrent =
      !currentPoint || (!currentPoint.lat && !currentPoint.lon);

    let insertIndex;

    // если точка пустая, заменяем ее
    if (isEmptyCurrent) {
      current.splice(index, 1, ...pointsFromClipboard);
      insertIndex = index;
    } else {
      insertIndex = index + 1;
      current.splice(insertIndex, 0, ...pointsFromClipboard);
    }

    form.setFieldsValue({ points: current });
    commit(current);

    // подсветка новых точек
    const newIndexes = new Set();
    for (let i = 0; i < pointsFromClipboard.length; i++) {
      newIndexes.add(insertIndex + i);
    }
    setHighlighted(newIndexes);
  };

  // Обработчик вставки по кнопке из буфера
  const handlePasteFromClipboardToEnd = useCallback(async () => {
    try {
      if (!navigator.clipboard) {
        message.warning(
          "Браузер не поддерживает чтение буфера обмена. Используйте Ctrl+V",
        );
        return;
      }

      const permission = await navigator.permissions
        ?.query({ name: "clipboard-read" })
        .catch(() => null);

      if (permission?.state === "denied") {
        message.warning("Доступ к буферу обмена запрещен. Используйте Ctrl+V");
        return;
      }

      const text = await navigator.clipboard.readText();
      if (!text) return;

      const pointsFromClipboard = parseClipboardPoints(text);

      if (
        pointsFromClipboard.length === 1 &&
        pointsFromClipboard[0].lon === null
      ) {
        return;
      }

      if (!pointsFromClipboard.length) {
        message.warning("В буфере нет корректных координат.");
        return;
      }

      const current = [...(form.getFieldValue("points") || [])];
      const insertIndex = current.length;
      const next = [...current, ...pointsFromClipboard];

      form.setFieldsValue({ points: next });
      commit(next);

      const newIndexes = new Set();
      for (let i = insertIndex; i < next.length; i++) {
        newIndexes.add(i);
      }

      setHighlighted(newIndexes);
      setSelectAll(false);
    } catch (err) {
      message.warning(
        "Не удалось получить доступ к буферу обмена. Используйте Ctrl+V.",
      );
    }
  }, [form, commit]);

  useEffect(() => {
    if (!visible) return;

    const handler = (e) => {
      const active = document.activeElement;

      // Если вставка происходит в input — не трогаем
      if (
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          active.isContentEditable)
      ) {
        return;
      }

      const text = e.clipboardData?.getData("text");
      if (!text) return;

      const pointsFromClipboard = parseClipboardPoints(text);

      if (
        pointsFromClipboard.length === 1 &&
        pointsFromClipboard[0].lon === null
      ) {
        return;
      }

      if (!pointsFromClipboard.length) return;

      e.preventDefault();

      const current = [...(form.getFieldValue("points") || [])];
      const insertIndex = current.length;
      const next = [...current, ...pointsFromClipboard];

      form.setFieldsValue({ points: next });
      commit(next);

      const newIndexes = new Set();
      for (let i = insertIndex; i < next.length; i++) {
        newIndexes.add(i);
      }

      setHighlighted(newIndexes);
      setSelectAll(false);
    };

    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [visible, form, commit]);

  // Обработчик вставки значений из файла
  const handleUploadCSV = (file) => {
    const isCSV =
      file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");

    if (!isCSV) {
      message.warning("Загружаемый файл не соответствует формату .csv");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result;
      const points = parseCSVPoints(text);

      if (!points.length) {
        message.warning("Файл не содержит корректных координат.");
        return;
      }

      const current = [...(form.getFieldValue("points") || [])];
      const next = [...current, ...points];

      form.setFieldsValue({ points: next });
      commit(next);

      const newIndexes = new Set();
      for (let i = current.length; i < next.length; i++) {
        newIndexes.add(i);
      }
      setHighlighted(newIndexes);
    };

    reader.readAsText(file);

    return false;
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async (values) => {
    // Преобразуем точки в нужный формат
    const points = values.points
      ? values.points.map(({ lat, lon }) => ({
          lat: parseFloat(lat),
          lon: parseFloat(lon),
        }))
      : [];

    // Проверяем, что минимум 3 точки для полигона
    if (points.length < 3) {
      setError("Полигон должен содержать минимум 3 точки.");
      return;
    }

    onSave(points);
    form.resetFields();
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        if (info?.errorFields?.length) {
          const firstError = info.errorFields[0];
          const pointIndex = firstError.name?.[1];

          if (typeof pointIndex === "number") {
            scrollToPointIndex(pointIndex);
          }
        }
      });
  };

  const scrollToPointIndex = (index) => {
    const el = itemRefs.current[index];
    const container = listRef.current;

    if (!el || !container) return;

    const elementTop = el.offsetTop;
    const elementBottom = elementTop + el.offsetHeight;

    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;

    let targetScroll = viewTop;

    if (elementTop < viewTop) {
      targetScroll = elementTop - 16;
    }

    if (elementBottom > viewBottom) {
      targetScroll = elementBottom - container.clientHeight + 16;
    }

    container.scrollTo({
      top: targetScroll,
      behavior: "smooth",
    });

    requestAnimationFrame(() => {
      el.querySelector("input")?.focus();
    });
  };

  return (
    <Modal
      className={styles.polygon_modal}
      maskClosable={false}
      title="Управление точками полигона"
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText="Сохранить"
      cancelText="Отмена"
      width={MODAL_WIDTH}
    >
      <Flex gap={20} className={styles.polygon_modal_content}>
        <div className={styles.polygon_map}>
          <InputPolygonOnMap
            field={field}
            objectUuid={objectUuid}
            value={currentPoints}
            onChange={handleChangePoints}
            color={color}
            highlightedIndexes={highlighted}
            hoveredIndex={hoveredIndex}
            onHoverIndex={(i) => {
              setHoverSource("map");
              setHoveredIndex(i);
            }}
            style={{ height: "100%" }}
          />
        </div>

        <Form
          form={form}
          onFinish={handleSave}
          className={styles.polygon_form}
          style={{ "--polygon-form-width": `${FORM_WIDTH}px` }}
          onFieldsChange={(changedFields) => {
            if (!changedFields.some((f) => f.name?.[0] === "points")) return;

            clearTimeout(blurTimeout.current);
            blurTimeout.current = setTimeout(() => {
              commit(form.getFieldValue("points"));
            }, 300);
          }}
        >
          <Flex
            justify="space-between"
            align="center"
            className={styles.polygon_toolbar}
            style={{ marginBottom: 8 }}
          >
            <Checkbox
              tabIndex="-1"
              checked={selectAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setSelectAll(checked);

                if (checked && formValues?.points) {
                  setSelectedPoints(
                    new Set(formValues.points.map((_, index) => index)),
                  );
                } else {
                  setSelectedPoints(new Set());
                }
              }}
            >
              Выделить все
            </Checkbox>

            <Flex gap={8}>
              <Tooltip title={copied ? "Скопировано" : "Копировать координаты"}>
                <Button
                  type="default"
                  icon={copied ? <CheckIcon /> : <DuplicateIcon />}
                  disabled={selectedPoints.size === 0}
                  onClick={copy}
                  className={styles.polygon_action_btn}
                />
              </Tooltip>

              <Tooltip title="Удалить выделенные точки">
                <Button
                  type="default"
                  danger
                  icon={<TrashIcon />}
                  disabled={selectedPoints.size === 0}
                  onClick={handleRemoveSelected}
                  className={styles.polygon_action_btn}
                />
              </Tooltip>

              <Tooltip title="Вставить координаты из буфера">
                <Button
                  type="default"
                  icon={<PlusIcon />}
                  onClick={handlePasteFromClipboardToEnd}
                  className={styles.polygon_action_btn}
                />
              </Tooltip>

              <Tooltip title="Вставить координаты из файла .csv">
                <Upload
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={handleUploadCSV}
                >
                  <Button
                    type="default"
                    icon={<DocumentAddIcon />}
                    className={styles.polygon_action_btn}
                  ></Button>
                </Upload>
              </Tooltip>

              <Tooltip title="Отменить последнее действие">
                <Button
                  type="default"
                  icon={<ReplyIcon />}
                  disabled={!canUndo}
                  onClick={() => {
                    setHighlighted(new Set());
                    undo();
                  }}
                  className={styles.polygon_action_btn}
                />
              </Tooltip>
            </Flex>
          </Flex>

          <Form.List name="points">
            {(fields, { add, remove, move }) => (
              <Flex vertical gap={18}>
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={({ active, over }) => {
                    if (!over || active.id === over.id) return;

                    const oldIndex = fields.findIndex(
                      (f) => f.key === active.id,
                    );
                    const newIndex = fields.findIndex((f) => f.key === over.id);
                    if (oldIndex === -1 || newIndex === -1) return;

                    move(oldIndex, newIndex);

                    const next = [...formValues.points];
                    const [moved] = next.splice(oldIndex, 1);
                    next.splice(newIndex, 0, moved);

                    commit(next);
                  }}
                >
                  <SortableContext
                    items={fields.map((f) => f.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Flex
                      ref={listRef}
                      vertical
                      gap={10}
                      className={styles.polygon_points_list}
                      style={{
                        maxHeight: LIST_MAX_HEIGHT,
                        overflowY: "auto",
                      }}
                    >
                      {fields.map(({ key, name, ...restField }, index) => (
                        <SortablePointItem
                          key={key}
                          id={key}
                          name={name}
                          index={index}
                          itemRef={(el) => {
                            if (el) itemRefs.current[index] = el;
                          }}
                          restField={restField}
                          highlighted={highlighted.has(index)}
                          onPaste={handlePastePoints}
                          onRemove={(name) => {
                            const next = [...(formValues?.points || [])];
                            next.splice(index, 1);

                            setHighlighted(new Set());
                            remove(name);
                            commit(next);
                          }}
                          checked={selectedPoints.has(index)}
                          onToggle={() => {
                            setSelectedPoints((prev) => {
                              const next = new Set(prev);
                              if (next.has(index)) {
                                next.delete(index);
                              } else {
                                next.add(index);
                              }
                              setSelectAll(false);
                              return next;
                            });
                          }}
                          hovered={hoveredIndex === index}
                          onHover={() => {
                            setHoverSource("list");
                            setHoveredIndex(index);
                          }}
                          onLeave={() => setHoveredIndex(null)}
                        />
                      ))}
                    </Flex>
                  </SortableContext>
                </DndContext>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      error && setError(null);
                      const next = [
                        ...(formValues?.points || []),
                        { lat: null, lon: null },
                      ];
                      add({ lat: null, lon: null });
                      setSelectAll(false);
                      commit(next);
                    }}
                    block
                    icon={<PlusIcon />}
                  >
                    Добавить точку
                  </Button>
                </Form.Item>

                {error && <Text type="danger">{error}</Text>}
              </Flex>
            )}
          </Form.List>
        </Form>
      </Flex>
    </Modal>
  );
};

// Компонент для сортируемого элемента списка
function SortablePointItem({
  id,
  name,
  index,
  restField,
  checked,
  highlighted,
  hovered,
  itemRef,
  onToggle,
  onRemove,
  onPaste,
  onHover,
  onLeave,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const memoizedValue = useMemo(
    () => ({
      attributes,
      listeners,
      setActivatorNodeRef: setNodeRef,
    }),
    [attributes, listeners, setNodeRef],
  );

  return (
    <SortableListItemContext.Provider value={memoizedValue}>
      <div
        ref={(el) => {
          setNodeRef(el);
          if (itemRef) itemRef(el);
        }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onFocus={onHover}
        onBlur={onLeave}
        style={{
          ...style,
          borderRadius: "4px",
          border: hovered
            ? "1px solid #52c41a"
            : highlighted
              ? "1px solid #1677ff"
              : "1px solid #d9d9d9",

          backgroundColor: hovered
            ? "#f6ffed"
            : highlighted
              ? "#e6f4ff"
              : "#fafafa",
          padding: "5px 12px 10px",
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 10 }}
        >
          <Flex align="center" gap={10}>
            <DragHandle
              tabIndex="-1"
              context={SortableListItemContext}
              style={{ marginLeft: "-4px" }}
            />
            <Text strong>Точка {index + 1}</Text>
          </Flex>

          <Button
            tabIndex="-1"
            type="text"
            danger
            icon={<TrashIcon />}
            onClick={() => onRemove(name)}
            style={{ marginRight: "-8px" }}
          />
        </Flex>
        <Flex align="start">
          <div style={{ marginRight: 10, marginBlock: "auto" }}>
            <Checkbox tabIndex="-1" checked={checked} onChange={onToggle} />
          </div>

          <Flex gap={20} style={{ flex: 1 }}>
            <Form.Item
              {...restField}
              label="Широта"
              name={[name, "lat"]}
              rules={[
                { required: true, message: "Введите широту" },
                COORDINATE_RULE,
                LATITUDE_RULE,
              ]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input
                placeholder="Например: 55.7558"
                onPaste={(e) => onPaste(e, index)}
              />
            </Form.Item>

            <Form.Item
              {...restField}
              label="Долгота"
              name={[name, "lon"]}
              rules={[
                { required: true, message: "Введите долготу" },
                COORDINATE_RULE,
                LONGITUDE_RULE,
              ]}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <Input
                placeholder="Например: 37.6173"
                onPaste={(e) => onPaste(e, index)}
              />
            </Form.Item>
          </Flex>
        </Flex>
      </div>
    </SortableListItemContext.Provider>
  );
}

export default PolygonOnMapModal;
